import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { Icon, Skeleton, Tooltip } from '~/components';
import type { TerritoryCode } from '~/lib/territories/territories';
import { TerritoryPicker } from '~/modules/artist-roster/components/TrackDetails/components/TerritoryPicker/TerritoryPicker';
import { useArtistDetailsQuery } from '~/modules/artist-roster/hooks/useArtistDetailsQuery';

import { useBenchmarkQuery } from '../hooks/useBenchmarkQuery';
import { CampaignBenchmarksTable } from './CampaignBenchmarksTable';
import { ObjectivePicker } from './ObjectivePicker/ObjectivePicker';
import { PlatformsPicker } from './PlatformsPicker/PlatformsPicker';

const BENCHMARKS_URL_PARAMS = {
  PARTY_ID: 'partyId',
  OBJECTIVE: 'objective',
  PLATFORMS: 'platforms',
};

export function CampaignBenchmarksPage() {
  const { t } = useTranslation('campaign-planner', {
    keyPrefix: 'benchmarks',
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const partyId = searchParams.get(BENCHMARKS_URL_PARAMS.PARTY_ID) || undefined;
  const objectiveParam =
    searchParams.get(BENCHMARKS_URL_PARAMS.OBJECTIVE) || undefined;
  const platformsParam =
    searchParams.get(BENCHMARKS_URL_PARAMS.PLATFORMS) || undefined;

  const [objective, setObjective] = useState(objectiveParam || 'awareness');
  const [platforms, setPlatforms] = useState<string[]>(
    platformsParam ? platformsParam.split(',') : [],
  );
  const [selectedTerritories, setSelectedTerritories] = useState<
    TerritoryCode[]
  >([]);

  const { data: artistData, isLoading: isLoadingArtist } =
    useArtistDetailsQuery({ partyId });

  const {
    data: benchmarksData,
    error,
    isLoading: isLoadingBenchmarks,
    isFetching: isFetchingBenchmarks,
  } = useBenchmarkQuery({
    uaid: artistData?.uaid?.toString(),
    objective,
    platforms,
    territoryCodes: selectedTerritories,
  });

  const handleObjectiveChange = (value: string) => {
    setObjective(value);
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);

      newParams.set(BENCHMARKS_URL_PARAMS.OBJECTIVE, value);

      return newParams;
    });
  };

  const handlePlatformsChange = (value: string[]) => {
    setPlatforms(value);
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);

      newParams.set(BENCHMARKS_URL_PARAMS.PLATFORMS, value.join(','));

      return newParams;
    });
  };

  if (!partyId) {
    return (
      <div className="flex h-full items-center justify-center">
        <span>{t('errors.noPartyId')}</span>
      </div>
    );
  }

  let content;

  if (isLoadingArtist) {
    content = (
      <div className="flex w-full flex-col space-y-sm">
        <Skeleton className="h-md w-[10rem] rounded-sm bg-level-04" />
        <Skeleton className="h-md w-[8rem] rounded-sm bg-level-04" />
      </div>
    );
  } else if (artistData) {
    content = (
      <div className="flex items-center gap-sm">
        {artistData.image ? (
          <img
            src={artistData.image}
            alt={artistData.name}
            className="size-2xl rounded-full object-cover"
            loading="lazy"
          />
        ) : (
          <span className="flex size-2xl items-center justify-center rounded-full bg-level-03">
            <Icon
              type="user"
              variant="light"
              aria-label={t('labels.emptyAvatar')}
            />
          </span>
        )}
        <span className="font-bold">{artistData.name}</span>
      </div>
    );
  } else {
    content = null;
  }

  return (
    <div className="flex h-full flex-col gap-lg">
      <div className="flex flex-col gap-lg py-md">
        <div className="flex items-end justify-between">
          <div className="flex flex-col">
            <header className="flex items-center gap-sm">
              <h1 className="text-headline-sm font-bold">{t('title')}</h1>
              <Tooltip
                content={t('titleTooltip')}
                delayDuration={0}
                side="bottom"
                sideOffset={8}
                align="end"
                size="sm"
                contentClassName="max-w-[300px] px-md py-sm"
              >
                <Icon
                  type="info-circle"
                  variant="light"
                  size="xs"
                  aria-label={t('title')}
                />
              </Tooltip>
            </header>
            <span>{t('description')}</span>
          </div>
        </div>

        {/* Filter section */}
        <div className="flex items-center gap-md">
          <div className="flex flex-col items-start gap-sm pr-xl">
            <span>{t('labels.artist')}</span>
            {content}
          </div>
          <div className="flex flex-1 flex-col items-start gap-sm">
            <span>{t('labels.marketingObjective')}</span>
            <ObjectivePicker
              value={objective}
              onChange={handleObjectiveChange}
            />
          </div>
          <div className="flex flex-1 flex-col items-start gap-sm">
            <span>{t('labels.markets')}</span>
            <TerritoryPicker
              initialSelectedTerritories={selectedTerritories}
              onApply={setSelectedTerritories}
              triggerClassName="w-full"
            />
          </div>
          <div className="flex flex-1 flex-col items-start gap-sm">
            <span>{t('labels.platforms')}</span>
            <PlatformsPicker
              value={platforms}
              onApply={handlePlatformsChange}
            />
          </div>
        </div>
      </div>
      <CampaignBenchmarksTable
        data={benchmarksData ?? []}
        error={error}
        isLoadingBenchmarks={isLoadingBenchmarks}
        isFetchingBenchmarks={isFetchingBenchmarks}
      />
    </div>
  );
}

CampaignBenchmarksPage.displayName = 'CampaignBenchmarksPage';
